import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PRICING_PLANS } from '@/constants/pricing';

export interface AdminUser {
  id: string;
  user_id: string | null;
  email: string | null;
  full_name: string | null;
  plan: string;
  total_credits: number;
  monthly_images_used: number;
  monthly_scripts_used: number;
  monthly_audio_minutes_used: number;
  images_limit: number;
  scripts_limit: number;
  audio_minutes_limit: number;
  subscription_status: string;
  is_banned: boolean;
  banned_at: string | null;
  banned_reason: string | null;
  daily_generation_limit: number;
  notes: string | null;
  created_at: string;
  role: string;
}

export const useAdminUsers = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('admin_get_all_users');

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUserRole = async (profileId: string, newRole: string) => {
    try {
      const user = users.find(u => u.id === profileId);
      const userId = user?.user_id || profileId;

      // Delete existing role
      await supabase.from('user_roles').delete().eq('user_id', userId);

      // Insert new role if not 'user' (default)
      if (newRole !== 'user') {
        const { error } = await supabase
          .from('user_roles')
          .insert([{ user_id: userId, role: newRole as any }]);

        if (error) throw error;
      }

      toast.success('User role updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const updateUserPlan = async (profileId: string, newPlan: string) => {
    try {
      const { error } = await supabase.rpc('admin_update_user_plan', {
        p_profile_id: profileId,
        p_plan: newPlan
      });

      if (error) throw error;

      toast.success(`Plan updated to ${newPlan} with correct limits`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user plan:', error);
      toast.error('Failed to update user plan');
    }
  };

  const updateUserLimits = async (
    profileId: string, 
    limits: {
      imagesLimit?: number;
      scriptsLimit?: number;
      audioMinutesLimit?: number;
      dailyLimit?: number;
      credits?: number;
    }
  ) => {
    try {
      const { error } = await supabase.rpc('admin_update_user_limits', {
        p_profile_id: profileId,
        p_images_limit: limits.imagesLimit ?? null,
        p_scripts_limit: limits.scriptsLimit ?? null,
        p_audio_minutes_limit: limits.audioMinutesLimit ?? null,
        p_daily_limit: limits.dailyLimit ?? null,
        p_credits: limits.credits ?? null
      });

      if (error) throw error;

      toast.success('User limits updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user limits:', error);
      toast.error('Failed to update user limits');
    }
  };

  const updateUserCredits = async (profileId: string, credits: number) => {
    return updateUserLimits(profileId, { credits });
  };

  const resetUserUsage = async (profileId: string) => {
    try {
      const { error } = await supabase.rpc('admin_reset_user_usage', {
        p_profile_id: profileId
      });

      if (error) throw error;

      toast.success('User usage reset successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error resetting user usage:', error);
      toast.error('Failed to reset user usage');
    }
  };

  const suspendUser = async (profileId: string, reason?: string) => {
    try {
      const user = users.find(u => u.id === profileId);
      const isBanned = user?.is_banned;

      const { error } = await supabase
        .from('profiles')
        .update({ 
          is_banned: !isBanned,
          banned_at: !isBanned ? new Date().toISOString() : null,
          banned_reason: !isBanned ? (reason || 'Suspended by admin') : null
        })
        .eq('id', profileId);

      if (error) throw error;

      toast.success(isBanned ? 'User unbanned successfully' : 'User banned successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error('Failed to update user status');
    }
  };

  useEffect(() => {
    fetchUsers();

    // Real-time subscription for profile updates
    const channel = supabase
      .channel('admin-users-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles' },
        () => fetchUsers()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_roles' },
        () => fetchUsers()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchUsers]);

  return {
    users,
    loading,
    fetchUsers,
    updateUserRole,
    updateUserPlan,
    updateUserLimits,
    updateUserCredits,
    resetUserUsage,
    suspendUser
  };
};
